import React, { useState, useRef, useEffect } from "react";
import { i18n } from "@euroland/libs";
import { TOOL_TEMPLATE, TAB } from "../constant/common";
import { useTemplateSetting, useCompanySettings } from "../hooks/useCompanySettings";
import { useInstrument } from "../hooks/useInstruments";
import useSaveLinkDownload from "../hooks/useSaveLinkDownload";
import AnnualClosePrices from "./AnnualClosePrices";
import HistoricalSelection from "./HistoricalSelection";
import QuadrataViewHistory from "./QuadrataViewHistory";
import { formatDateApi, checkEmptyObject, compareDate, compareSameKeyObject } from "../utils";
import VictorViewHistory from "./VictorViewHistory";
import useHistoricalShares from "../hooks/useHistoricalShares";
import { getAnnualClosePricesAction } from "../actions";
import { useDispatch, useSelector } from "react-redux";
import { instrumentsSettingsSelector } from "../store/slice/settings/selectors";
import { annualClosePricesSelector, instrumentsIndexerSelector } from "../store/slice/trade";

const HistoryTabContent = ({ currentTab, isChangeTab }) => {
  const prevLoading = useRef(false);
  const historicalSelectionRef = useRef();

  const dispatch = useDispatch();
  const [showData, showDataSet] = useState(false);
  const template = useTemplateSetting();

  const [{ selectedInstrument }] = useInstrument();
  const instrumentsSettings = useSelector(instrumentsSettingsSelector);
  const instrumentDataIndexer = useSelector(instrumentsIndexerSelector);
  const annualPrices = useSelector(annualClosePricesSelector);

  const { saveLinkDownLoad } = useSaveLinkDownload();
  const { enableExcelDownload, showAnnualClosePrices } = useCompanySettings();
  const {
    getHistoricalShares,
    historicalShares: { historicalShareList, loading },
  } = useHistoricalShares();
  const prevShowData = useRef({});
  const firstRender = useRef(true);

  useEffect(() => {
    if (prevLoading.current && !loading) showDataSet(true);
    prevLoading.current = loading;
  }, [loading]);

  // when instrument selected in share-price tab dif in  historical tab, we fetch data
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
    } else if (
      TAB.history === currentTab &&
      !checkEmptyObject(prevShowData.current) &&
      !compareSameKeyObject(prevShowData.current.prevSelectedInstrument, selectedInstrument)
    ) {
      fetchData(prevShowData.current, selectedInstrument);
    }
  }, [isChangeTab]);

  // On first get data, when we have annual close price data
  // but each data have number of year less than 1, display card
  useEffect(() => {
    if (
      // template === TOOL_TEMPLATE.victor &&
      annualPrices.loaded &&
      annualPrices.annualClosePrices.length > 0 &&
      annualPrices.isAllLessTwoYearAnnualClosePrices
    ) {
      historicalSelectionRef.current?.handleShowDataRef();
    }
  }, [annualPrices.annualClosePrices]);

  const getAnnualShare = () => {
    const shares = [];
    for (let item of instrumentsSettings) {
      shares.push({
        id: item.id,
        isin: instrumentDataIndexer[item.id].iSIN,
        marketId: instrumentDataIndexer[item.id].marketID,
        currency: instrumentDataIndexer[item.id].currencyCode,
      });
    }

    return shares;
  };

  const compareShowData = currentData => {
    const { currency = "", pickerDate = {} } = currentData;
    // Check prevShowData have no data
    if (checkEmptyObject(prevShowData.current)) return;
    const noChangeInstrument = compareSameKeyObject(
      prevShowData.current.prevSelectedInstrument,
      selectedInstrument
    );
    return (
      noChangeInstrument &&
      prevShowData.current.currency === currency &&
      compareDate(prevShowData.current.pickerDate.from, pickerDate.from, "day")
    );
  };

  const fetchData = (showDatas, instrumentTicker) => {
    prevShowData.current = { ...showDatas, prevSelectedInstrument: instrumentTicker };

    const { currency = "", pickerDate = {} } = showDatas;
    const shares = [];
    for (let x in instrumentTicker) {
      shares.push({
        id: instrumentTicker[x].instrumentId,
        isin: instrumentTicker[x].iSIN,
        marketId: instrumentTicker[x].marketID,
        currency: instrumentTicker[x].currencyCode,
      });
    }

    const dataQuery = {
      currency,
      fromDate: pickerDate?.from ? formatDateApi(pickerDate.from) : null,
      toDate: null,
      shares,
    };

    getHistoricalShares(dataQuery);
    if (enableExcelDownload) saveLinkDownLoad(pickerDate, currency);
  };

  const handleShowData = showDatas => {
    if (showData && (loading || compareShowData(showDatas))) return;

    const { currency = "" } = showDatas;
    if ((prevShowData.current.currency || "") !== currency) {
      dispatch(getAnnualClosePricesAction({ shares: getAnnualShare(), currency }));
    }

    fetchData(showDatas, selectedInstrument);
  };

  return (
    <>
      <HistoricalSelection ref={historicalSelectionRef} onSubmit={handleShowData} loading={loading} />

      {showData && template === TOOL_TEMPLATE.victor && historicalShareList?.length > 0 && (
        <VictorViewHistory onClose={() => showDataSet(false)} />
      )}
      {showData && template === TOOL_TEMPLATE.quadrata && historicalShareList?.length > 0 && (
        <QuadrataViewHistory onClose={() => showDataSet(false)} />
      )}

      {showData && historicalShareList.length === 0 && (
        <p className="no-data-text" role='note'>{i18n.translate("noData")}</p>
      )}

      {showAnnualClosePrices && !showData && <AnnualClosePrices />}
    </>
  );
};

export default HistoryTabContent;
